import React, { useState } from "react";
import { ScrollView, View, StyleSheet } from "react-native";
import { useRouter, useSegments } from "expo-router";
import { TextInput } from "@/components/ui/TextInput";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { InlineDropdown } from "@/components/ui/InlineDropdown";
import { useTheme } from "@/hooks/useTheme";
import { ThemedText } from "@/components/ThemedText";
import { ApplicationType, APPLICATION_STATUSES } from "@/types/db/applications";
import type { ApplicationDataType } from "@/types/db/applications";
import { CreateHeader } from "@/components/screens/shared";
import useToast from "@/hooks/useToast";
import { applicationService } from "@/services/applicationService";
import { useRowSelectionContext } from "@/providers/RowSelectionProvider";
import useAuth from "@/hooks/useAuth";
import Head from "expo-router/head";
import { theme } from "@/constants/themes";
import { getSaveBehavior } from "@/lib/saveSettings";

const US_STATES = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
  { label: "District of Columbia", value: "DC" },
];

const REFERRAL_SOURCES = [
  { label: "Facebook", value: "Facebook" },
  { label: "Google", value: "Google" },
  { label: "Referral", value: "Referral" },
  { label: "Other", value: "Other" },
];

const DEFAULT_APP_DATA: ApplicationDataType = {
  authorizedToWork: false,
  over18: false,
  previouslyWorked: null,
  firstName: "",
  lastName: "",
  middleInitial: "",
  preferredName: "",
  email: "",
  phone: "",
  streetLine1: "",
  streetLine2: "",
  city: "",
  state: "",
  zip: "",
  office: "",
  referralSource: "",
  referralName: "",
  physicalRequirements: Array(11).fill(false),
  heatInjury: null,
  hasTransportation: null,
  eventServicesExperience: null,
  hasCellPhone: null,
  hasAdditionalInfo: null,
  additionalInfo: "",
};

// Status options for dropdown
const STATUS_OPTIONS = APPLICATION_STATUSES.map((status) => ({
  text: status,
  color:
    status === "DRAFT"
      ? "todo"
      : status === "REVIEWING"
        ? "pending"
        : status === "APPROVED"
          ? "complete"
          : status === "REJECTED"
            ? "attention"
            : "neutral",
}));

export function getApplicationStatusColor(status: string, colors: theme): string {
  switch (status) {
    case "DRAFT":
      return colors.todo.border;
    case "REVIEWING":
      return colors.pending.border;
    case "APPROVED":
      return colors.complete.border;
    case "REJECTED":
      return colors.attention.border;
    case "WITHDRAWN":
      return colors.neutral?.border || colors.backgroundSecondary;
    default:
      return colors.backgroundSecondary;
  }
}

export const APPLICATION_STATUS_OPTIONS = STATUS_OPTIONS;

export default function NewApplicationPage() {
  const toast = useToast();
  const router = useRouter();
  const segments = useSegments();
  const { colors } = useTheme();
  const { tenantId } = useAuth();
  const { triggerRefresh } = useRowSelectionContext();
  const isFullScreen = segments.includes("applications" as never);

  const [appData, setAppData] = useState<ApplicationDataType>({ ...DEFAULT_APP_DATA });
  const [status, setStatus] = useState<ApplicationType["status"]>("DRAFT");
  const [notes, setNotes] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const statusItems = APPLICATION_STATUSES.map((s) => ({
    text: s,
    bgColor: getApplicationStatusColor(s, colors),
  }));

  const updateAppData = (updates: Partial<ApplicationDataType>) => {
    setAppData((prev) => ({ ...prev, ...updates }));
    setDirty(true);
  };

  const updateStatus = (value: string) => {
    setStatus(value as ApplicationType["status"]);
    setDirty(true);
  };

  const updateNotes = (value: string) => {
    setNotes(value);
    setDirty(true);
  };

  const showReferralName = appData.referralSource === "Referral" || appData.referralSource === "Other";

  const handleSave = async () => {
    if (!appData.firstName.trim() || !appData.lastName.trim() || !appData.email.trim()) {
      toast.showToast({
        title: "Missing required fields",
        message: "Please fill in first name, last name, and email",
        toastType: "error",
      });
      return;
    }

    setSaving(true);
    try {
      const createdApp = await applicationService.createApplication({
        tenant_id: tenantId!,
        status,
        meta_data: {
          application_data: appData,
          notes: notes.trim() || undefined,
          source: "web",
          submittedAt: new Date().toISOString(),
        },
      });
      toast.showToast({ title: "Application created successfully", toastType: "success" });
      triggerRefresh("application");

      const behavior = await getSaveBehavior("application");
      switch (behavior) {
        case "view_created":
          router.setParams({ new: undefined, id: createdApp?.id });
          break;
        case "create_another":
          handleReset();
          break;
        case "close":
        default:
          router.setParams({ new: undefined, panel: undefined });
          break;
      }
    } catch (err: any) {
      toast.showToast({
        title: "Failed to create application",
        message: err.message,
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setAppData({ ...DEFAULT_APP_DATA });
    setStatus("DRAFT");
    setNotes("");
    setDirty(false);
  };

  return (
    <>
      <Head>
        <title>NEXUS New Application</title>
        <meta name="description" content="" />
      </Head>
      <View style={styles.container}>
        <CreateHeader
          entityType="application"
          useFirstLastName
          firstName={appData.firstName}
          onFirstNameChange={(value) => updateAppData({ firstName: value })}
          lastName={appData.lastName}
          onLastNameChange={(value) => updateAppData({ lastName: value })}
          dirty={dirty}
          saving={saving}
          onSave={handleSave}
          onCancel={handleReset}
          onClose={isFullScreen ? () => router.back() : undefined}
          subtitle={appData.email || undefined}
          status={status}
          onStatusChange={updateStatus}
          statusOptions={statusItems}
        />
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.content}>
          {/* Personal Information */}
          <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <ThemedText style={styles.sectionTitle}>Personal Information</ThemedText>
            <TextInput
              label="M.I."
              value={appData.middleInitial || ""}
              onChangeText={(text) => updateAppData({ middleInitial: text.slice(0, 1) })}
              editable
            />
            <TextInput
              label="Preferred Name"
              value={appData.preferredName || ""}
              onChangeText={(text) => updateAppData({ preferredName: text })}
              editable
            />
            <TextInput
              label="Email *"
              value={appData.email}
              onChangeText={(text) => updateAppData({ email: text })}
              editable
            />
            <TextInput
              label="Phone"
              value={appData.phone}
              onChangeText={(text) => updateAppData({ phone: text })}
              editable
            />
          </View>

          {/* Address */}
          <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <ThemedText style={styles.sectionTitle}>Address</ThemedText>
            <TextInput
              label="Street Line 1"
              value={appData.streetLine1}
              onChangeText={(text) => updateAppData({ streetLine1: text })}
              editable
            />
            <TextInput
              label="Street Line 2"
              value={appData.streetLine2 || ""}
              onChangeText={(text) => updateAppData({ streetLine2: text })}
              editable
            />
            <TextInput
              label="City"
              value={appData.city}
              onChangeText={(text) => updateAppData({ city: text })}
              editable
            />
            <InlineDropdown
              options={US_STATES}
              value={appData.state}
              placeholder="State"
              onSelect={(value) => updateAppData({ state: value })}
            />
            <TextInput
              label="ZIP"
              value={appData.zip}
              onChangeText={(text) => updateAppData({ zip: text.replace(/\D/g, "").slice(0, 5) })}
              editable
            />
          </View>

          {/* Office & Referral */}
          <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <ThemedText style={styles.sectionTitle}>Office & Referral</ThemedText>
            <TextInput
              label="Office"
              value={appData.office || ""}
              onChangeText={(text) => updateAppData({ office: text })}
              editable
            />
            <InlineDropdown
              options={REFERRAL_SOURCES}
              value={appData.referralSource}
              placeholder="Referral Source"
              onSelect={(value) => updateAppData({ referralSource: value, referralName: "" })}
            />
            {showReferralName && (
              <TextInput
                label="Referral Name"
                value={appData.referralName || ""}
                onChangeText={(text) => updateAppData({ referralName: text })}
                editable
              />
            )}
          </View>

          {/* Eligibility */}
          <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <ThemedText style={styles.sectionTitle}>Eligibility</ThemedText>
            <View style={[styles.toggleRow, { borderColor: colors.border }]}>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.toggleLabel}>Authorized to work in U.S.</ThemedText>
              </View>
              <ToggleSwitch
                value={appData.authorizedToWork}
                onValueChange={(value) => updateAppData({ authorizedToWork: value })}
              />
            </View>
            <View style={[styles.toggleRow, { borderColor: colors.border }]}>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.toggleLabel}>Over 18</ThemedText>
              </View>
              <ToggleSwitch value={appData.over18} onValueChange={(value) => updateAppData({ over18: value })} />
            </View>
            <View style={[styles.toggleRow, { borderColor: colors.border }]}>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.toggleLabel}>Previously worked for NEXUS</ThemedText>
              </View>
              <ToggleSwitch
                value={appData.previouslyWorked ?? false}
                onValueChange={(value) => updateAppData({ previouslyWorked: value })}
              />
            </View>
          </View>

          {/* Additional Questions */}
          <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <ThemedText style={styles.sectionTitle}>Additional Questions</ThemedText>
            <View style={[styles.toggleRow, { borderColor: colors.border }]}>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.toggleLabel}>Heat-related injury history</ThemedText>
              </View>
              <ToggleSwitch
                value={appData.heatInjury ?? false}
                onValueChange={(value) => updateAppData({ heatInjury: value })}
              />
            </View>
            <View style={[styles.toggleRow, { borderColor: colors.border }]}>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.toggleLabel}>Has transportation</ThemedText>
              </View>
              <ToggleSwitch
                value={appData.hasTransportation ?? false}
                onValueChange={(value) => updateAppData({ hasTransportation: value })}
              />
            </View>
            <View style={[styles.toggleRow, { borderColor: colors.border }]}>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.toggleLabel}>Event services experience</ThemedText>
              </View>
              <ToggleSwitch
                value={appData.eventServicesExperience ?? false}
                onValueChange={(value) => updateAppData({ eventServicesExperience: value })}
              />
            </View>
            <View style={[styles.toggleRow, { borderColor: colors.border }]}>
              <View style={{ flex: 1 }}>
                <ThemedText style={styles.toggleLabel}>Has smartphone</ThemedText>
              </View>
              <ToggleSwitch
                value={appData.hasCellPhone ?? false}
                onValueChange={(value) => updateAppData({ hasCellPhone: value })}
              />
            </View>
            <TextInput
              label="Additional info"
              value={appData.additionalInfo || ""}
              onChangeText={(text) => updateAppData({ additionalInfo: text, hasAdditionalInfo: text.length > 0 })}
              multiline
              numberOfLines={3}
              editable
            />
          </View>

          {/* Notes */}
          <View style={[styles.section, { backgroundColor: colors.card, borderColor: colors.border }]}>
            <ThemedText style={styles.sectionTitle}>Admin Notes</ThemedText>
            <TextInput label="Notes" value={notes} onChangeText={updateNotes} multiline numberOfLines={3} editable />
          </View>
        </ScrollView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 16,
    gap: 12,
  },
  section: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    gap: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 4,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingVertical: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  toggleLabel: {
    fontSize: 14,
    fontWeight: "500",
  },
});
